import { useCallback, useEffect, useState } from "react";
import { getApiUrl } from "../../utils/api";
import { ensureFreshAccessToken } from "../../utils/auth";

/**
 * @typedef {Object} BugTicket
 * @property {string} issueKey
 * @property {string} [title]
 * @property {string} [status]
 * @property {string} [priority]
 * @property {string} [product]
 * @property {string} [namespace]
 * @property {string} [description]
 * @property {number} chunkCount
 * @property {number} chunksFound
 * @property {{chunkIndex:number,pointId:string,text:string,length:number}[]} chunks
 * @property {string[]} warnings
 */

// Per-key cache so a re-render or a remount never refetches. Failures are not cached.
const cache = new Map();
const inflight = new Map();

const load = (key) => {
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    const token = await ensureFreshAccessToken();
    const res = await fetch(getApiUrl(`/api/bugs/${encodeURIComponent(key)}`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 404) {
      const err = new Error("not found");
      err.notFound = true;
      throw err;
    }
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    const data = await res.json();
    cache.set(key, data);
    return data;
  })().finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
};

/** @returns {{data: BugTicket|null, loading: boolean, error: string, notFound: boolean, retry: () => void}} */
export default function useBugTicket(issueKey) {
  const [state, setState] = useState({
    data: cache.get(issueKey) || null,
    loading: !cache.has(issueKey),
    error: "",
    notFound: false,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    if (cache.has(issueKey)) {
      setState({ data: cache.get(issueKey), loading: false, error: "", notFound: false });
      return undefined;
    }
    setState({ data: null, loading: true, error: "", notFound: false });
    load(issueKey)
      .then((data) => live && setState({ data, loading: false, error: "", notFound: false }))
      .catch((e) =>
        live &&
        setState({
          data: null,
          loading: false,
          error: e.notFound ? "" : e.message || "Something went wrong",
          notFound: !!e.notFound,
        })
      );
    return () => {
      live = false;
    };
  }, [issueKey, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
